import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { CheckBounceReasonDetailsEntity } from '../../../../api/types/settings/bouncing-check-reasons/bouncing-check-reasons.type';
import dateFormat from '../../../../utils/date-format';
import CallToActionButton from '../../../../component/call-to-action-button/call-to-action-button';
import { WarningDialog } from '../../../../component/ui/warning-dialog/warning-dialog';
import { getCheckBounceReasonsListOperation } from './operations/get-check-bounce-reasons-list-operation';
import { deleteCheckBounceReasonOperation } from './operations/delete-check-bounce-reason-operation';
import { AddCheckBounceReasonDialog } from './componenets/add-check-bounce-reason-dialog';
import { EditCheckBounceReasonDialog } from './componenets/edit-check-bounce-reason-dialog';

export const BouncingCheckReasonsTab = () => {
  const [data, setData] = useState<CheckBounceReasonDetailsEntity[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);

  // Add dialog visibility
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);

  // Delete confirmation state
  const [rowToDelete, setRowToDelete] =
    useState<CheckBounceReasonDetailsEntity | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  // Edit dialog state (null = closed, otherwise the row being edited)
  const [rowToEdit, setRowToEdit] =
    useState<CheckBounceReasonDetailsEntity | null>(null);

  const fetchPage = useCallback(
    async (nextOffset: number, nextLimit: number) => {
      await getCheckBounceReasonsListOperation(
        nextOffset,
        nextLimit,
        setData,
        setTotal,
        setLoading,
      );
    },
    [],
  );

  useEffect(() => {
    fetchPage(offset, limit);
    // mount-only; subsequent fetches go through onPageChange / refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCurrentPage = useCallback(async () => {
    await fetchPage(offset, limit);
  }, [fetchPage, offset, limit]);

  const onPageChange = async (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    await fetchPage(newOffset, newLimit);
  };

  const handleDeleteClick = (row: CheckBounceReasonDetailsEntity) => {
    setRowToDelete(row);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    await deleteCheckBounceReasonOperation(
      rowToDelete.id,
      setDeleteLoading,
      async () => {
        setRowToDelete(null);
        await refreshCurrentPage();
      },
    );
  };

  const handleDeleteHide = () => {
    if (deleteLoading) return;
    setRowToDelete(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div
        style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}
      >
        <CallToActionButton
          text={'הוספה'}
          onClick={() => setShowAddDialog(true)}
        />
      </div>

      <div
        style={{
          fontSize: '14px',
          color: '#4b5563',
          textAlign: 'right',
        }}
      >
        סה״כ רשומות: {total.toLocaleString()}
      </div>

      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        value={data}
        loading={loading}
        lazy
        paginator
        rows={limit}
        first={offset}
        totalRecords={total}
        onPage={onPageChange}
        rowsPerPageOptions={[10, 20, 50, 100]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        dataKey="id"
        tableStyle={{ direction: 'rtl' }}
      >
        <Column
          field="paymentServiceName"
          header="שם שרות"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="paymentServiceTypeName"
          header="סוג שרות"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="paymentServicePrice"
          header="מחיר"
          style={{ textAlign: 'right', width: '120px' }}
          body={(row: CheckBounceReasonDetailsEntity) =>
            typeof row.paymentServicePrice === 'number'
              ? `₪${Number(row.paymentServicePrice).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : 'חסר'
          }
        />
        <Column
          field="bounceReason"
          header="סיבת חזרה"
          style={{ textAlign: 'right' }}
          body={(row: CheckBounceReasonDetailsEntity) =>
            row.bounceReason?.trim() ? row.bounceReason : 'חסר'
          }
        />
        <Column
          field="createdByName"
          header="נוצר ע״י"
          style={{ textAlign: 'right', width: '160px' }}
          body={(row: CheckBounceReasonDetailsEntity) =>
            row.createdByName?.trim() ? row.createdByName : 'חסר'
          }
        />
        <Column
          field="createdAt"
          header="נוצר בתאריך"
          style={{ textAlign: 'right', width: '140px' }}
          body={(row: CheckBounceReasonDetailsEntity) =>
            row.createdAt
              ? dateFormat.displayFormatDate(new Date(row.createdAt))
              : ''
          }
        />
        <Column
          header=""
          style={{ textAlign: 'right', width: '120px' }}
          body={(row: CheckBounceReasonDetailsEntity) => (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                icon="pi pi-pencil"
                text
                severity="warning"
                tooltip="עריכת סיבת חזרה"
                tooltipOptions={{ position: 'top' }}
                disabled={deleteLoading}
                onClick={() => setRowToEdit(row)}
              />
              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                tooltip="מחיקת סיבת חזרה"
                tooltipOptions={{ position: 'top' }}
                loading={deleteLoading && rowToDelete?.id === row.id}
                disabled={deleteLoading && rowToDelete?.id !== row.id}
                onClick={() => handleDeleteClick(row)}
              />
            </div>
          )}
        />
      </DataTable>

      <WarningDialog
        header="מחיקת סיבת חזרה"
        isVisible={rowToDelete !== null}
        onHide={handleDeleteHide}
        addIcon
        content="האם אתה בטוח שברצונך למחוק את סיבת החזרה?"
        quoteName={rowToDelete?.bounceReason ?? undefined}
        dangerousContent="פעולה זו אינה ניתנת לביטול"
        onConfirm={handleDeleteConfirm}
        confirmLabel="מחיקה"
        confirmLoading={deleteLoading}
      />

      <AddCheckBounceReasonDialog
        visible={showAddDialog}
        onHide={() => setShowAddDialog(false)}
        onCreated={refreshCurrentPage}
      />

      <EditCheckBounceReasonDialog
        visible={rowToEdit !== null}
        onHide={() => setRowToEdit(null)}
        onUpdated={refreshCurrentPage}
        row={rowToEdit}
      />
    </div>
  );
};
